import { useState, useEffect } from "react";
import { FaYoutube } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "./LatestVideos.css";
import { useTranslation } from "../../i18n";
import { renderBrandText } from "../../utils/brandText";
import { fetchTextFromApiCandidates } from "../../utils/api";

const fallbackVideos = [
    {
        id: "OH3H1F2hbkI",
        title: "Herramientas que todo QA deberia conocer en 2025",
        publishedAt: "2025-01-20T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=OH3H1F2hbkI",
        thumbnail: "https://i.ytimg.com/vi/OH3H1F2hbkI/hqdefault.jpg",
        description: "",
    },
    {
        id: "jzgn377TgWA",
        title: "Cypress: Querying (cy.get, contains, selectors)",
        publishedAt: "2024-12-01T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=jzgn377TgWA",
        thumbnail: "https://i.ytimg.com/vi/jzgn377TgWA/hqdefault.jpg",
        description: "",
    },
    {
        id: "RDsJl_reY70",
        title: "Introduccion a Playwright",
        publishedAt: "2024-10-14T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=RDsJl_reY70",
        thumbnail: "https://i.ytimg.com/vi/RDsJl_reY70/hqdefault.jpg",
        description: "",
    },
    {
        id: "LLJt1alI0vg",
        title: "Introduccion a Postman",
        publishedAt: "2024-09-02T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=LLJt1alI0vg",
        thumbnail: "https://i.ytimg.com/vi/LLJt1alI0vg/hqdefault.jpg",
        description: "",
    },
    {
        id: "tvQTni3PXAc",
        title: "Introduccion a Cucumber",
        publishedAt: "2024-08-11T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=tvQTni3PXAc",
        thumbnail: "https://i.ytimg.com/vi/tvQTni3PXAc/hqdefault.jpg",
        description: "",
    },
    {
        id: "Y-lvGx4Nebo",
        title: "Introduccion a Cypress",
        publishedAt: "2024-07-06T00:00:00.000Z",
        link: "https://www.youtube.com/watch?v=Y-lvGx4Nebo",
        thumbnail: "https://i.ytimg.com/vi/Y-lvGx4Nebo/hqdefault.jpg",
        description: "",
    },
];


export default function LatestVideos() {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [videos, setVideos] = useState(fallbackVideos);

    const normalizeVideos = (items) =>
        items
            .filter((video) => video?.id && video?.title && video?.link)
            .slice(0, 6);

    useEffect(() => {
        // Verifica si la vista es móvil
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
    
        checkMobile(); // Verifica al cargar
        window.addEventListener("resize", checkMobile);
    
        // Obtener videos de YouTube
        async function fetchYouTubeVideos() {
            try {
                const { text } = await fetchTextFromApiCandidates("/api/youtube-feed", { timeoutMs: 2500 });
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, "text/xml");
                const items = xml.getElementsByTagName("entry");
    
                const videosData = Array.from(items).map((item) => ({
                    id: item.getElementsByTagName("yt:videoId")[0]?.textContent || 
                        item.getElementsByTagName("videoId")[0]?.textContent,
                    title: item.getElementsByTagName("title")[0]?.textContent,
                    publishedAt: item.getElementsByTagName("published")[0]?.textContent,
                    link: item.getElementsByTagName("link")[0]?.getAttribute("href"),
                    thumbnail: item.getElementsByTagName("media:thumbnail")[0]?.getAttribute("url"),
                    description: item.getElementsByTagName("media:description")[0]?.textContent || "",
                }));

                const normalizedVideos = normalizeVideos(videosData);
                setVideos(normalizedVideos.length > 0 ? normalizedVideos : fallbackVideos);
            } catch (error) {
                console.error("Error al obtener los videos de YouTube:", error);
                setVideos(fallbackVideos);
            }
        }
    
        fetchYouTubeVideos();
    
        // Cleanup del event listener
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    const renderVideoCard = (video) => (
        <div className="latest-videos-card">
            <div className="latest-videos-image-wrap">
                <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="latest-videos-image"
                />
                <span className="latest-videos-badge">{renderBrandText(t("latestVideos.badge"))}</span>
            </div>
            <div className="latest-videos-content">
                <div className="latest-videos-meta">
                    <span className="latest-videos-label">{t("latestVideos.recentLabel")}</span>
                    <p className="latest-videos-date">
                        {t("common.publishedOn")} {new Date(video.publishedAt).toLocaleDateString()}
                    </p>
                </div>
                <h3 className="latest-videos-title-text">{video.title}</h3>
                <div className="latest-videos-actions">
                    <a
                        href={video.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="latest-videos-watch-button"
                    >
                        <span className="latest-videos-watch-icon">
                            <FaYoutube />
                        </span>
                        <span className="latest-videos-watch-copy">
                            <strong>{t("common.watchYoutube")}</strong>
                            <small>{t("latestVideos.openEpisode")}</small>
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );


    return (
        <section className="latest-videos-container">
            <div className="latest-videos-heading">
                <div className="latest-videos-heading-copy">
                    <span className="latest-videos-kicker">{t("latestVideos.kicker")}</span>
                    <h2 className="latest-videos-title">{t("learn.videosTitle")}</h2>
                    <p className="latest-videos-intro">{t("learn.videosIntro")}</p>
                </div>
                <a
                    href="https://www.youtube.com/@theqahub_es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="latest-videos-channel-link"
                >
                    <span className="latest-videos-channel-icon">
                        <FaYoutube />
                    </span>
                    <span className="latest-videos-channel-copy">
                        <strong>{renderBrandText(t("latestVideos.channelTitle"))}</strong>
                        <small>{t("latestVideos.channelSubtitle")}</small>
                    </span>
                </a>
            </div>
            <>
                {isMobile ? (
                    <>
                        <Swiper
                            key={videos.length}
                            modules={[Pagination, Autoplay]}
                            spaceBetween={20}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            autoplay={{ delay: 3000, disableOnInteraction: false }} // 🔥 Autoplay cada 3 segundos
                            loop={videos.length > 1}
                            centeredSlides={false}
                            watchOverflow
                            style={{ width: "100%", maxWidth: "1200px", height: "auto" }}
                            className="latest-videos-swiper"
                        >
                            {videos.length > 0 ? (
                                videos.map((video) => (
                                    <SwiperSlide key={video.id}>
                                        {renderVideoCard(video)}
                                    </SwiperSlide>
                                ))
                            ) : (
                                <p className="latest-videos-no-results">{t("common.noVideos")}</p>
                            )}
                        </Swiper>
                    </>
                ) : (
                    <>
                        <Swiper
                            key={videos.length}
                            modules={[Pagination, Autoplay]}
                            spaceBetween={28}
                            slidesPerView={3}
                            pagination={{ clickable: true }}
                            autoplay={{ delay: 3000, disableOnInteraction: false }} // 🔥 Autoplay cada 3 segundos
                            loop={videos.length > 3}
                            centeredSlides={false}
                            watchOverflow
                            breakpoints={{
                                0: {
                                    slidesPerView: 1,
                                    spaceBetween: 20
                                },
                                900: {
                                    slidesPerView: 2,
                                    spaceBetween: 24
                                },
                                1280: {
                                    slidesPerView: 3,
                                    spaceBetween: 28
                                }
                            }}
                            style={{ width: "100%", maxWidth: "1200px", height: "auto" }}
                            className="latest-videos-swiper"
                        >
                            {videos.length > 0 ? (
                                videos.map((video) => (
                                    <SwiperSlide key={video.id}>
                                        {renderVideoCard(video)}
                                    </SwiperSlide>
                                ))
                            ) : (
                                <p className="latest-videos-no-results">{t("common.noVideos")}</p>
                            )}
                        </Swiper>
                    </>
                )}
            </>
        </section>
    );
}
